import React, { useMemo } from 'react';
import { Button, Dropdown } from 'antd';

import { useItemActions } from './ItemActionsMenu';
import { buildActionMenu } from '../common/actionMenu';
import { KEBAB } from '../icons';

/**
 * A board card's ⋯ (KPR-1395): the item's own menu (`useItemActions`, every
 * action it offers anywhere) with **Move to…** first in its Record group — a
 * submenu of the board's columns, the column the card is in disabled. It is
 * the phone's way to move a card (a phone has no drag) and the keyboard's.
 * A card that cannot move says why on the entry itself (`moveDisabled`).
 *
 *   <BoardItemActionsMenu {...itemActionProps}
 *     moveTargets={[{ key, label, current, disabled, title }]}
 *     onMove={column => …}
 *     moveDisabled={reason | null} />
 */
export default function BoardItemActionsMenu({
	moveTargets = [],
	onMove,
	moveDisabled = null,
	buttonProps = {},
	placement = 'bottomRight',
	className = '',
	...actionProps
}) {
	const { actions, modals } = useItemActions(actionProps);
	const menu = useMemo(() => {
		const move = moveTargets.length ? {
			key: 'board-move',
			group: 'record',
			glyph: 'items',
			// A card that cannot move says why in the entry itself, readable
			// without a hover (a phone has none).
			label: moveDisabled ? (
				<span className="board-menu__move">
					<span>Move to…</span>
					<span className="board-menu__reason">{moveDisabled}</span>
				</span>
			) : 'Move to…',
			disabled: Boolean(moveDisabled),
			title: moveDisabled || undefined,
			children: moveDisabled ? undefined : moveTargets.map(target => ({
				key: `board-move:${target.key}`,
				label: target.label,
				disabled: Boolean(target.current || target.disabled),
				title: target.title || undefined,
				onClick: () => onMove?.(target),
			})),
		} : null;
		// A card that cannot move already says why on Move to… just above, and
		// the cause is the one that shuts Change card: its own reason line would
		// say it twice, so it keeps the reason as a title only here (KPR-710).
		const rest = (actions || []).map(a => (moveDisabled && a.unavailable ? { ...a, unavailable: undefined, disabled: true, title: a.unavailable } : a));
		const built = buildActionMenu([...(move ? [move] : []), ...rest]);
		// A tap opens Move to… (a phone has no hover to open a submenu by),
		// and so does a click or Enter on a desktop.
		return built ? { ...built, triggerSubMenuAction: 'click' } : built;
	}, [actions, moveTargets, onMove, moveDisabled]);
	if (!menu) return null;
	return (
		<>
			<Dropdown trigger={['click']} placement={placement} menu={menu}>
				<Button icon={<KEBAB />} aria-label="Item actions" className={className || undefined} {...buttonProps} />
			</Dropdown>
			{modals}
		</>
	);
}
